import React, { useState } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  Button,
  DataTable,
  Modal,
  Badge,
  EmptyState,
} from "@shopify/polaris";
import { BADGE_CONDITIONS, TIERS, TIER_NAMES } from "../../shared/types.js";

const CONDITION_OPTIONS = [
  { label: "Siparis Sayisi", value: "order_count" },
  { label: "Toplam Harcama (TL)", value: "total_spent" },
  { label: "Kategori Siparis Sayisi", value: "category_count" },
  { label: "Tier Seviyesi", value: "tier_reached" },
];

export default function Badges() {
  const [badges, setBadges] = useState(getMockBadges());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    icon: "",
    conditionType: "order_count",
    conditionValue: "",
  });

  function openCreateModal() {
    setEditingBadge(null);
    setForm({ name: "", description: "", icon: "", conditionType: "order_count", conditionValue: "" });
    setModalOpen(true);
  }

  function openEditModal(badge) {
    setEditingBadge(badge);
    setForm({
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      conditionType: badge.conditionType,
      conditionValue: String(badge.conditionValue),
    });
    setModalOpen(true);
  }

  function handleSave() {
    if (editingBadge) {
      setBadges((prev) =>
        prev.map((b) => (b.id === editingBadge.id ? { ...b, ...form, conditionValue: form.conditionValue } : b))
      );
    } else {
      setBadges((prev) => [
        ...prev,
        { id: `badge_${Date.now()}`, ...form, earnedCount: 0 },
      ]);
    }
    setModalOpen(false);
  }

  function handleDelete(id) {
    setBadges((prev) => prev.filter((b) => b.id !== id));
  }

  const rows = badges.map((badge) => [
    <InlineStack gap="200" blockAlign="center">
      <span style={{ fontSize: 20 }}>{badge.icon || "🏅"}</span>
      <Text fontWeight="bold">{badge.name}</Text>
    </InlineStack>,
    badge.description,
    CONDITION_OPTIONS.find((o) => o.value === badge.conditionType)?.label || badge.conditionType,
    badge.conditionValue,
    badge.earnedCount,
    <InlineStack gap="200">
      <Button size="slim" onClick={() => openEditModal(badge)}>
        Duzenle
      </Button>
      <Button size="slim" tone="critical" onClick={() => handleDelete(badge.id)}>
        Sil
      </Button>
    </InlineStack>,
  ]);

  return (
    <Page
      title="Rozetler"
      subtitle="Musteri basari rozetlerini yonet"
      primaryAction={{
        content: "Yeni Rozet",
        onAction: openCreateModal,
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            {badges.length === 0 ? (
              <EmptyState
                heading="Henuz rozet tanimlanmamis"
                action={{ content: "Rozet Olustur", onAction: openCreateModal }}
              >
                <p>Musterilerinizi motive etmek icin basari rozetleri olusturun.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "text", "text", "numeric", "text"]}
                headings={["Rozet", "Aciklama", "Kosul Tipi", "Kosul Degeri", "Kazanan", "Islem"]}
                rows={rows}
                hoverable
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingBadge ? "Rozet Duzenle" : "Yeni Rozet Olustur"}
        primaryAction={{ content: "Kaydet", onAction: handleSave }}
        secondaryActions={[{ content: "Iptal", onAction: () => setModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Rozet Adi"
              value={form.name}
              onChange={(v) => setForm((p) => ({ ...p, name: v }))}
              autoComplete="off"
              placeholder="ornek: RC Araba Tutkunu"
            />
            <TextField
              label="Aciklama"
              value={form.description}
              onChange={(v) => setForm((p) => ({ ...p, description: v }))}
              autoComplete="off"
              placeholder="ornek: 3 veya daha fazla RC araba siparisi verin"
            />
            <TextField
              label="Ikon (emoji)"
              value={form.icon}
              onChange={(v) => setForm((p) => ({ ...p, icon: v }))}
              autoComplete="off"
              placeholder="ornek: 🏎️"
            />
            <Select
              label="Kosul Tipi"
              options={CONDITION_OPTIONS}
              value={form.conditionType}
              onChange={(v) => setForm((p) => ({ ...p, conditionType: v }))}
            />
            <TextField
              label={form.conditionType === "tier_reached" ? "Tier (rookie/pro/legend)" : "Kosul Degeri"}
              value={form.conditionValue}
              onChange={(v) => setForm((p) => ({ ...p, conditionValue: v }))}
              autoComplete="off"
              type={form.conditionType === "tier_reached" ? "text" : "number"}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

function getMockBadges() {
  return [
    { id: "ilk_pit_stop", name: "İlk Pit Stop", description: "Hoş geldin, yarış başladı!", icon: "🏁", conditionType: "order_count", conditionValue: "1", earnedCount: 423 },
    { id: "garaj_ustasi", name: "Garaj Ustası", description: "5+ sipariş ver", icon: "🔧", conditionType: "order_count", conditionValue: "5", earnedCount: 89 },
    { id: "koleksiyoncu", name: "Koleksiyoncu", description: "3+ farklı kategoriden alışveriş", icon: "🏆", conditionType: "category_count", conditionValue: "3", earnedCount: 67 },
    { id: "hiz_canavari", name: "Hız Canavarı", description: "Aynı ay 3+ sipariş", icon: "⚡", conditionType: "order_count", conditionValue: "3", earnedCount: 34 },
    { id: "full_gaz", name: "Full Gaz", description: "1.500 TL+ tek sipariş", icon: "🔥", conditionType: "total_spent", conditionValue: "1500", earnedCount: 156 },
    { id: "sadik_pilot", name: "Sadık Pilot", description: "3 ardışık ay sipariş", icon: "⭐", conditionType: "order_count", conditionValue: "3", earnedCount: 52 },
    { id: "rc_araba_tutkunu", name: "RC Araba Tutkunu", description: "RC Araba kategorisinden 3+ ürün", icon: "🏎️", conditionType: "category_count", conditionValue: "3", earnedCount: 67 },
    { id: "gokyuzu_hakimi", name: "Gökyüzü Hakimi", description: "RC Uçak kategorisinden 2+ ürün", icon: "✈️", conditionType: "category_count", conditionValue: "2", earnedCount: 28 },
    { id: "deniz_kurdu", name: "Deniz Kurdu", description: "RC Tekne kategorisinden 2+ ürün", icon: "🚤", conditionType: "category_count", conditionValue: "2", earnedCount: 19 },
    { id: "efsane_pilot", name: "Efsane Pilot", description: "Legend tier'a ulaş", icon: "👑", conditionType: "tier_reached", conditionValue: "legend", earnedCount: 46 },
    { id: "erken_kus", name: "Erken Kuş", description: "Programa ilk 100 kayıt", icon: "🐦", conditionType: "order_count", conditionValue: "1", earnedCount: 100 },
    { id: "dogum_gunu", name: "Doğum Günü", description: "Doğum gününde sipariş", icon: "🎂", conditionType: "order_count", conditionValue: "1", earnedCount: 12 },
  ];
}
